import React, {useEffect, useState} from "react";
import {Col, Form, Input, Modal, Row, Select, Switch, message} from "antd";
import {useTranslation} from "react-i18next";
import {useStore} from "hooks/useStore";
import {
    TICKET_CATEGORIES,
    TICKET_NON_AUTO_ASSIGN_ROLES,
    TICKET_PAIRED_ROLES,
    TICKET_EXCLUDED_ROLES,
    TICKET_PRIORITIES
} from "constants/ticket";
import {getAutoAssignRoleOptions, getTopicOptions} from "./ticketTypeOptions";

export const TicketTypeModal = ({visible, editData, options, onCancel, onSuccess}) => {
    const {t} = useTranslation(["tickets"]);
    const stores = useStore();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [category, setCategory] = useState(null);

    const isEditMode = !!editData;

    useEffect(() => {
        if (visible && editData) {
            form.setFieldsValue({
                category: editData.category,
                topic: editData.topic,
                name: editData.name,
                autoAssignRole: (editData.autoAssignRole || []).filter(
                    role =>
                        !TICKET_NON_AUTO_ASSIGN_ROLES.includes(role) &&
                        !TICKET_EXCLUDED_ROLES.includes(role)
                ),
                priority: editData.priority,
                isPhotoRequired: !!editData.isPhotoRequired
            });
            setCategory(editData.category);
        } else if (!visible) {
            form.resetFields();
            setCategory(null);
        }
    }, [visible, editData, form]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const payload = {
                category: values.category,
                topic: values.topic,
                name: values.name.trim(),
                priority: values.priority,
                autoAssignRole: values.autoAssignRole,
                isPhotoRequired: Boolean(values.isPhotoRequired),
                isActive: isEditMode ? editData.isActive : true
            };

            const result = isEditMode
                ? await stores.ticketTypeStore.updateTicketType(editData._id, payload)
                : await stores.ticketTypeStore.createTicketType(payload);

            if (result && result.success !== false) {
                onSuccess();
            }
        } catch (error) {
            if (error.errorFields) {
                message.error(t("ticketTypes.modal.requiredFields", {ns: "tickets"}));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRoleSelect = role => {
        const selectedRoles = form.getFieldValue("autoAssignRole") || [];
        const allowedKeys = getAutoAssignRoleOptions(options).map(option => option.key);
        const missingPairedRoles = (TICKET_PAIRED_ROLES[role] || []).filter(
            pairedRole => allowedKeys.includes(pairedRole) && !selectedRoles.includes(pairedRole)
        );

        if (missingPairedRoles.length) {
            form.setFieldsValue({autoAssignRole: [...selectedRoles, ...missingPairedRoles]});
        }
    };

    return (
        <Modal
            title={
                isEditMode
                    ? t("ticketTypes.modal.editTitle", {ns: "tickets"})
                    : t("ticketTypes.modal.createTitle", {ns: "tickets"})
            }
            visible={visible}
            onOk={handleSubmit}
            onCancel={onCancel}
            confirmLoading={loading}
            okText={t("ticketTypes.modal.save", {ns: "tickets"})}
            cancelText={t("ticketTypes.modal.cancel", {ns: "tickets"})}
            width={600}
        >
            <Form form={form} layout="vertical" initialValues={{isPhotoRequired: false}}>
                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label={t("ticketTypes.modal.category", {ns: "tickets"})}
                            name="category"
                            rules={[
                                {
                                    required: true,
                                    message: t("ticketTypes.modal.categoryRequired", {
                                        ns: "tickets"
                                    })
                                }
                            ]}
                        >
                            <Select
                                placeholder={t("ticketTypes.modal.categoryPlaceholder", {
                                    ns: "tickets"
                                })}
                                onChange={value => {
                                    setCategory(value);
                                    form.setFieldsValue({topic: undefined});
                                }}
                            >
                                {TICKET_CATEGORIES.map(item => (
                                    <Select.Option key={item} value={item}>
                                        {t(`categories.${item}`, {ns: "tickets"})}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label={t("ticketTypes.modal.topic", {ns: "tickets"})}
                            name="topic"
                            rules={[
                                {
                                    required: true,
                                    message: t("ticketTypes.modal.topicRequired", {ns: "tickets"})
                                }
                            ]}
                        >
                            <Select
                                disabled={!category}
                                placeholder={
                                    category
                                        ? t("ticketTypes.modal.topicPlaceholder", {ns: "tickets"})
                                        : t("ticketTypes.modal.topicDisabledPlaceholder", {
                                              ns: "tickets"
                                          })
                                }
                            >
                                {getTopicOptions(options, category).map(topic => (
                                    <Select.Option key={topic.key} value={topic.key}>
                                        {topic.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item
                    label={t("ticketTypes.modal.name", {ns: "tickets"})}
                    name="name"
                    rules={[
                        {
                            required: true,
                            whitespace: true,
                            message: t("ticketTypes.modal.nameRequired", {ns: "tickets"})
                        },
                        {max: 200, message: t("ticketTypes.modal.nameMax", {ns: "tickets"})}
                    ]}
                >
                    <Input.TextArea
                        rows={2}
                        placeholder={t("ticketTypes.modal.namePlaceholder", {ns: "tickets"})}
                    />
                </Form.Item>
                <Form.Item
                    label={t("ticketTypes.modal.roles", {ns: "tickets"})}
                    name="autoAssignRole"
                    rules={[
                        {
                            required: true,
                            type: "array",
                            min: 1,
                            message: t("ticketTypes.modal.rolesRequired", {ns: "tickets"})
                        }
                    ]}
                >
                    <Select
                        mode="multiple"
                        showSearch
                        optionFilterProp="children"
                        placeholder={t("ticketTypes.modal.rolesPlaceholder", {ns: "tickets"})}
                        onSelect={handleRoleSelect}
                    >
                        {getAutoAssignRoleOptions(options).map(role => (
                            <Select.Option key={role.key} value={role.key}>
                                {role.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item
                    label={t("ticketTypes.modal.priority", {ns: "tickets"})}
                    name="priority"
                    rules={[
                        {
                            required: true,
                            message: t("ticketTypes.modal.priorityRequired", {ns: "tickets"})
                        }
                    ]}
                >
                    <Select
                        placeholder={t("ticketTypes.modal.priorityPlaceholder", {ns: "tickets"})}
                    >
                        {TICKET_PRIORITIES.map(priority => (
                            <Select.Option key={priority} value={priority}>
                                {t(`priorities.${priority}`, {ns: "tickets"})}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item
                    label={t("ticketTypes.modal.photoRequired", {ns: "tickets"})}
                    name="isPhotoRequired"
                    valuePropName="checked"
                    style={{marginBottom: 0}}
                >
                    <Switch />
                </Form.Item>
            </Form>
        </Modal>
    );
};
